import { Link, useLocation } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { NavItem } from "@/components/layout/nav-items";
import { cn } from "@/lib/utils";

interface NavDropdownProps {
  item: NavItem;
  onNavigate?: () => void;
}

function isPathActive(pathname: string, path: string): boolean {
  if (path === "/") return pathname === "/";
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function NavDropdown({ item, onNavigate }: NavDropdownProps) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const clearClose = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  }, []);

  const handleOpen = useCallback(() => {
    clearClose();
    setOpen(true);
  }, [clearClose]);

  const handleClose = useCallback(() => {
    clearClose();
    closeTimeout.current = setTimeout(() => setOpen(false), 120);
  }, [clearClose]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && e.target instanceof Node && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDocClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [open]);

  const children = item.children ?? [];
  const childActive = children.some((c) => isPathActive(location.pathname, c.path));
  const active = childActive;

  return (
    <div ref={rootRef} className="relative" onMouseEnter={handleOpen} onMouseLeave={handleClose}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        onFocus={handleOpen}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          "relative flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-[12.5px] font-medium transition-colors",
          active ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/10 hover:text-white",
        )}
      >
        <span>{item.label}</span>
        <ChevronDown className="h-3 w-3 opacity-70" />
        {active && (
          <span className="absolute -bottom-[1px] left-2 right-2 h-[2px] rounded-full bg-primary" />
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-40 mt-1 min-w-[220px] overflow-hidden rounded-lg border border-border bg-card shadow-xl"
        >
          <div className="py-1">
            {children.map((child) => {
              const cActive = isPathActive(location.pathname, child.path);
              const Icon = child.icon;
              return (
                <Link
                  key={child.path}
                  to={child.path}
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onNavigate?.();
                  }}
                  className={cn(
                    "flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] transition-colors hover:bg-muted",
                    cActive ? "bg-muted text-foreground" : "text-muted-foreground",
                  )}
                  style={cActive ? { boxShadow: "inset 2px 0 0 var(--primary)" } : undefined}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="flex-1 truncate">{child.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
